import React, { createContext, useContext, useEffect, useState } from "react";
import axiosInstance from "../services/axiosInstance";

interface Screen {
  moduleScreenId: number;
  path: string;
  screenCode: string;
  screenName: string;
  icon: string;  
}

interface ModuleContextType {
  getScreenIdByPath: (path: string) => number | undefined;
  getScreenByPath: (path: string) => Screen | undefined;
  loading: boolean;
}

const ModuleContext = createContext<ModuleContextType | undefined>(undefined);

export const ModuleProvider = ({ children }: { children: React.ReactNode }) => {
  const [screens, setScreens] = useState<Screen[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadScreens = async () => {
      try {
        const res = await axiosInstance.get("/modules/screens?onlyActive=true");

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const flatScreens = res.data.flatMap((m: any) => m.screens || []);
        setScreens(flatScreens);
      } finally {
        setLoading(false);
      }
    };

    loadScreens();
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const normalize = (str: string) =>
    str.replace(/^\/+/, "").replace(/\/+$/, "").replace(/-/g, "/");

  const getScreenIdByPath = (path: string) => {
    if (!path) return undefined;

    const cleanPath = path.replace(/^\/+/, "");

    return screens.find((screen) => {
      if (!screen.path) return false; 

      const screenPath = (screen.path || "").replace(/^\/+/, "");

      if (cleanPath === screenPath) return true;

      if (cleanPath.startsWith(screenPath + "/")) return true;

      return false;
    })?.moduleScreenId;
  };

  const getScreenByPath = (path: string) => {
    if (!path) return undefined;

    const cleanPath = path.replace(/^\/+/, "");

    return screens.find((screen) => {
      if (!screen.path) return false;

      const screenPath = (screen.path || "").replace(/^\/+/, "");

      if (cleanPath === screenPath) return true;

      if (cleanPath.startsWith(screenPath + "/")) return true;

      return false;
    });
  };

  return (
    <ModuleContext.Provider value={{ getScreenIdByPath, getScreenByPath, loading }}>
      {children}
    </ModuleContext.Provider>
  );
};

export const useModule = () => {
  const context = useContext(ModuleContext);
  if (!context) throw new Error("useModule must be used within ModuleProvider");
  return context;
};
